// 登录后强制设支付密码守卫（2026-09-19-N10 S10b：2FA 改可选增强，不再是门禁条件）
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchMe } from '@/api/auth';
import { loadSession, saveSession, SESSION_CHANGED_EVENT } from '@/api/client';
import { getPreferredLang, isChdLang, localePath } from '@/i18n/localePath';
import { isImpersonatedSession } from '@/auth/impersonation';

function isPayPasswordPath(pathname: string): boolean {
  return pathname.includes('security/payment-password');
}

/** 连续失败达到该次数后，错误态追加 3423 口径的支付密码引导入口 */
const ME_FAILURE_GUIDE_THRESHOLD = 3;

/** 订阅 session 写入，供安全页在设置支付密码/绑 2FA 后重读标志 */
export function useSetupFlags() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    window.addEventListener(SESSION_CHANGED_EVENT, bump);
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, bump);
  }, []);
  const me = loadSession()?.me;
  return {
    mustChangePassword: me?.mustChangePassword === true,
    mfaSetupRequired: me?.mfaSetupRequired === true,
    payPasswordSetupRequired: me?.payPasswordSetupRequired === true,
  };
}

export default function RequireSetup() {
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { payPasswordSetupRequired } = useSetupFlags();
  /** loading=探测中；ready=/auth/me 成功；error=失败可重试（绝不放行 Outlet） */
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  /** 连续失败次数（成功即清零），达到阈值后展示 3423 口径引导 */
  const [failureCount, setFailureCount] = useState(0);
  /** 重试代次：自增触发 effect 重新探测 */
  const [attempt, setAttempt] = useState(0);
  const resolved = isChdLang(lang) ? lang : getPreferredLang();
  const payPasswordPath = localePath('/cardholder/profile/security/payment-password', resolved);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchMe();
        if (cancelled) {
          return;
        }
        if (res.ok) {
          const s = loadSession();
          if (s) {
            saveSession({ ...s, me: res.data });
          }
          setFailureCount(0);
          setPhase('ready');
          return;
        }
      } catch {
        // 网络异常/超时等与 ok:false 同口径：留在错误态，绝不 setReady 放行
      }
      if (cancelled) {
        return;
      }
      setFailureCount((n) => n + 1);
      setPhase('error');
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => {
    setPhase('loading');
    setAttempt((n) => n + 1);
  }, []);

  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas text-slate-400">
        <span className="text-sm font-medium">{t('common.loading')}</span>
      </div>
    );
  }
  if (phase === 'error') {
    // 3423 口径引导的落地页必须可达：与既有 payPasswordSetupRequired 门禁的「设置页路径豁免」一致，
    // 否则「去设置支付密码」会跳回本错误态形成死链；其余业务路由一律不渲染 Outlet。
    if (isPayPasswordPath(location.pathname)) {
      return <Outlet />;
    }
    const showGuide = failureCount >= ME_FAILURE_GUIDE_THRESHOLD;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-canvas px-6 text-center">
        <span className="text-sm font-medium text-slate-400">{t('common.error')}</span>
        {showGuide && (
          <p className="max-w-sm text-xs leading-relaxed text-slate-500">
            {t('security.ppNotSetGuide')}
          </p>
        )}
        <div className="mt-1 flex items-center gap-3">
          <button type="button" className="btn-secondary text-sm" onClick={retry}>
            {t('common.retry')}
          </button>
          {showGuide && (
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => navigate(payPasswordPath)}
            >
              {t('security.goSetPayPassword')}
            </button>
          )}
        </div>
      </div>
    );
  }
  // S10b：唯一门禁 = 未设支付密码（payPasswordSetupRequired）→ 引导设置页。
  // 2FA 不再是门禁条件（mfaSetupRequired 对 CARDHOLDER 恒 false；已设支付密码但未启 2FA → 放行）。
  // 代登录会话豁免（2026-10-23 · spec §13.4/§13.9）：后端 LoginSetupGate 已对 impersonated 豁免 3423，
  // 前端跟随——不豁免会被强制跳支付密码设置页，而该端点已在硬禁清单（诱导触碰被禁操作）。
  // me.payPasswordSetupRequired 按仓储实值计算（不看 impersonated），故须在此显式豁免。
  if (
    payPasswordSetupRequired &&
    !isPayPasswordPath(location.pathname) &&
    !isImpersonatedSession(loadSession())
  ) {
    return <Navigate to={payPasswordPath} replace={true} />;
  }
  return <Outlet />;
}
